import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { usePageView } from '@/analytics/pageTypes';
import { Trans, useTranslation } from 'react-i18next';
import { Layout } from '@/components/layout';
import Hero from '@/sections/Hero';
import type { GetStaticProps } from 'next';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import { LINKS } from '@/constants/links';
import { Link } from '@/components/links';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import { FaqIntro } from '@/sections/FaqIntro';
import MoreInfo from '@/sections/MoreInfo';
import IconDeferredPayment from '@/assets/icons/heroIcons/Deferred-payment.svg';
import IconThird from '@/assets/icons/heroIcons/Icon-third.svg';
import IconCard from '@/assets/icons/heroIcons/Card-front.svg';
import Image from 'next/image';
import { AppstoreButton, GooglePlayButton } from '@/components/buttons';
import Floating, { Boxes } from '@/sections/Floating';
import { getPageBannerBySlug, PageBanner } from '@/api/sanity/pageBanners';
import { SeoHeader } from '@/components/SeoHeader';
import { Text } from '@/components/typography';
import { useIsSelectedLanguage } from '@/hooks/useIsSelectedLanguage';
import IconShoppingCart from '@/assets/icons/heroIcons/Shopping-cart.svg';
import IconMobile from '@/assets/icons/heroIcons/Device-mobile.svg';
import IconUser from '@/assets/icons/heroIcons/User-group.svg';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import IconMaxi from '@/assets/icons/heroIcons/Maxi.svg';
interface Props extends IntlProps {
  pageBanner: PageBanner | null;
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Page(props) {
  const $ = _c(129);
  usePageView("mobile-app");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["core", "mobileApp"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    pageBanner,
    menuPromoBanners
  } = props;
  const isCsLanguage = useIsSelectedLanguage("cs");
  let t1;
  if ($[1] !== t) {
    t1 = t("mobileApp:seo.title");
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t) {
    t2 = t("mobileApp:seo.description");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <SeoHeader title={t1} description={t2} />;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="bgCircle absolute top-0 left-0 hidden h-[50rem] w-[50rem] -translate-x-1/3 -translate-y-1/3 rotate-180 lg:block" />;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== pageBanner?.title || $[10] !== t) {
    t5 = pageBanner?.title ?? t("mobileApp:heroSection.title");
    $[9] = pageBanner?.title;
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== pageBanner?.text || $[13] !== t) {
    t6 = pageBanner?.text ?? t("mobileApp:heroSection.paragraph");
    $[12] = pageBanner?.text;
    $[13] = t;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = pageBanner?.variant ?? "mockupMobile";
  const t8 = pageBanner ? pageBanner.buttons : null;
  let t9;
  if ($[15] !== t) {
    t9 = t("mobileApp:heroSection.imageAlt");
    $[15] = t;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t9) {
    t10 = <div className="hidden md:flex"><Image src={`/images/mobileApp/${process.env.NEXT_PUBLIC_LANGUAGE}/qr-code.png`} width={120} height={120} alt={t9} /></div>;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="flex gap-1 md:flex-col"><AppstoreButton mobileVariant="full" /><GooglePlayButton mobileVariant="full" /></div>;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t10) {
    t12 = <div className="flex items-center justify-center gap-8 md:justify-normal">{t10}{t11}</div>;
    $[20] = t10;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const t13 = pageBanner?.image?.url ?? `/images/sections/mobileApp/hero/${process.env.NEXT_PUBLIC_LANGUAGE}/hero.png`;
  let t14;
  if ($[22] !== pageBanner?.image?.alt || $[23] !== t) {
    t14 = pageBanner?.image?.alt ?? t("mobileApp:heroSection.imageAlt");
    $[22] = pageBanner?.image?.alt;
    $[23] = t;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t13 || $[26] !== t14) {
    t15 = {
      url: t13,
      alt: t14
    };
    $[25] = t13;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t12 || $[29] !== t15 || $[30] !== t5 || $[31] !== t6 || $[32] !== t7 || $[33] !== t8) {
    t16 = <Hero title={t5} text={t6} variant={t7} buttons={t8} customButton={t12} image={t15} />;
    $[28] = t12;
    $[29] = t15;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
    $[33] = t8;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="bg-blue-50"><SectionDelimiter color="bg-blue-100" background="bg-transparent" /></div>;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  let t19;
  if ($[36] !== t) {
    t18 = <Trans t={t} i18nKey="mobileApp:howItWorks.items.1.text" />;
    t19 = t("mobileApp:howItWorks.items.1.title");
    $[36] = t;
    $[37] = t18;
    $[38] = t19;
  } else {
    t18 = $[37];
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t) {
    t20 = t("mobileApp:howItWorks.items.1.title");
    $[39] = t;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== t20) {
    t21 = {
      alt: t20,
      src: `/images/sections/mobileApp/articles/${process.env.NEXT_PUBLIC_LANGUAGE}/1.png`
    };
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t18 || $[44] !== t19 || $[45] !== t21) {
    t22 = {
      anchorKey: "mobileApp.howItWorks.items.1.title",
      variant: "small",
      content: t18,
      title: t19,
      image: t21
    };
    $[43] = t18;
    $[44] = t19;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  let t24;
  if ($[47] !== t) {
    t23 = <Trans t={t} i18nKey="mobileApp:howItWorks.items.2.text" />;
    t24 = t("mobileApp:howItWorks.items.2.title");
    $[47] = t;
    $[48] = t23;
    $[49] = t24;
  } else {
    t23 = $[48];
    t24 = $[49];
  }
  let t25;
  if ($[50] !== t) {
    t25 = t("mobileApp:howItWorks.items.2.title");
    $[50] = t;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== t25) {
    t26 = {
      alt: t25,
      src: `/images/sections/mobileApp/articles/${process.env.NEXT_PUBLIC_LANGUAGE}/2.png`
    };
    $[52] = t25;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== t23 || $[55] !== t24 || $[56] !== t26) {
    t27 = {
      anchorKey: "mobileApp.howItWorks.items.2.title",
      variant: "small",
      content: t23,
      title: t24,
      image: t26,
      imagePosition: "right"
    };
    $[54] = t23;
    $[55] = t24;
    $[56] = t26;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== isCsLanguage || $[59] !== t) {
    t28 = isCsLanguage && {
      anchorKey: "mobileApp.howItWorks.items.3.title",
      variant: "small",
      content: <Trans t={t} i18nKey="mobileApp:howItWorks.items.3.text"><Link to={LINKS().card} analyticsParam="mobileAppHowItWorksCard" testId="mobileAppHowItWorksCard" className="hover:text-white" /></Trans>,
      title: t("mobileApp:howItWorks.items.3.title"),
      image: {
        alt: t("mobileApp:howItWorks.items.3.title"),
        src: "/images/sections/mobileApp/articles/cs/3.png"
      }
    };
    $[58] = isCsLanguage;
    $[59] = t;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== isCsLanguage || $[62] !== t) {
    t29 = isCsLanguage && {
      anchorKey: "mobileApp.howItWorks.items.4.title",
      variant: "small",
      content: <Trans t={t} i18nKey="mobileApp:howItWorks.items.4.text" />,
      title: t("mobileApp:howItWorks.items.4.title"),
      image: {
        alt: t("mobileApp:howItWorks.items.4.title"),
        src: "/images/sections/mobileApp/articles/cs/4.png"
      },
      imagePosition: "right"
    };
    $[61] = isCsLanguage;
    $[62] = t;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Link to={LINKS().registration} analyticsParam="mobileAppHowItWorksRegistration" testId="mobileAppHowItWorksRegistration" className="hover:text-white" />;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  let t32;
  if ($[65] !== t) {
    t31 = <Trans t={t} i18nKey="mobileApp:howItWorks.items.5.text">{t30}</Trans>;
    t32 = t("mobileApp:howItWorks.items.5.title");
    $[65] = t;
    $[66] = t31;
    $[67] = t32;
  } else {
    t31 = $[66];
    t32 = $[67];
  }
  let t33;
  if ($[68] !== t) {
    t33 = t("mobileApp:howItWorks.items.5.title");
    $[68] = t;
    $[69] = t33;
  } else {
    t33 = $[69];
  }
  let t34;
  if ($[70] !== t33) {
    t34 = {
      alt: t33,
      src: `/images/sections/mobileApp/articles/${process.env.NEXT_PUBLIC_LANGUAGE}/5.png`
    };
    $[70] = t33;
    $[71] = t34;
  } else {
    t34 = $[71];
  }
  let t35;
  if ($[72] !== t31 || $[73] !== t32 || $[74] !== t34) {
    t35 = {
      anchorKey: "mobileApp.howItWorks.items.5.title",
      variant: "small",
      content: t31,
      title: t32,
      image: t34
    };
    $[72] = t31;
    $[73] = t32;
    $[74] = t34;
    $[75] = t35;
  } else {
    t35 = $[75];
  }
  let t36;
  if ($[76] !== t22 || $[77] !== t27 || $[78] !== t28 || $[79] !== t29 || $[80] !== t35) {
    t36 = [t22, t27, t28, t29, t35].filter(Boolean);
    $[76] = t22;
    $[77] = t27;
    $[78] = t28;
    $[79] = t29;
    $[80] = t35;
    $[81] = t36;
  } else {
    t36 = $[81];
  }
  const t37 = t36 as Boxes;
  let t38;
  if ($[82] !== t) {
    t38 = t("mobileApp:howItWorks.title");
    $[82] = t;
    $[83] = t38;
  } else {
    t38 = $[83];
  }
  let t39;
  if ($[84] !== t37 || $[85] !== t38) {
    t39 = <Floating boxes={t37} title={t38} />;
    $[84] = t37;
    $[85] = t38;
    $[86] = t39;
  } else {
    t39 = $[86];
  }
  let t40;
  if ($[87] !== t) {
    t40 = t("mobileApp:mobAppPromo.title");
    $[87] = t;
    $[88] = t40;
  } else {
    t40 = $[88];
  }
  let t41;
  if ($[89] !== t40) {
    t41 = <Image src={`/images/sections/rewards/${process.env.NEXT_PUBLIC_LANGUAGE}/app.png`} alt={t40} width={600} height={603} />;
    $[89] = t40;
    $[90] = t41;
  } else {
    t41 = $[90];
  }
  let t42;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <b className="text-blue-500" />;
    $[91] = t42;
  } else {
    t42 = $[91];
  }
  let t43;
  if ($[92] !== t) {
    t43 = <h3 className="mb-4 text-center text-2xl font-bold text-gray-900 lg:mb-12 lg:text-left lg:text-5xl"><Trans t={t} i18nKey="mobileApp:mobAppPromo.title">{t42}</Trans></h3>;
    $[92] = t;
    $[93] = t43;
  } else {
    t43 = $[93];
  }
  let t44;
  if ($[94] !== t) {
    t44 = t("mobileApp:mobAppPromo.paragraph");
    $[94] = t;
    $[95] = t44;
  } else {
    t44 = $[95];
  }
  let t45;
  if ($[96] !== t44) {
    t45 = <Text variant="p2" className="text-center text-gray-800 lg:text-left">{t44}</Text>;
    $[96] = t44;
    $[97] = t45;
  } else {
    t45 = $[97];
  }
  let t46;
  if ($[98] !== t) {
    t46 = t("mobileApp:mobAppPromo.imageDescription");
    $[98] = t;
    $[99] = t46;
  } else {
    t46 = $[99];
  }
  let t47;
  if ($[100] !== t46) {
    t47 = <Image src={`/images/mobileApp/${process.env.NEXT_PUBLIC_LANGUAGE}/qr-code.png`} alt={t46} width={120} height={120} className="hidden lg:block" />;
    $[100] = t46;
    $[101] = t47;
  } else {
    t47 = $[101];
  }
  let t48;
  if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <div className="flex justify-between gap-2 lg:flex-col"><AppstoreButton mobileVariant="full" /><GooglePlayButton mobileVariant="full" /></div>;
    $[102] = t48;
  } else {
    t48 = $[102];
  }
  let t49;
  if ($[103] !== t47) {
    t49 = <div className="mt-10 flex gap-8 lg:mt-16">{t47}{t48}</div>;
    $[103] = t47;
    $[104] = t49;
  } else {
    t49 = $[104];
  }
  let t50;
  if ($[105] !== t43 || $[106] !== t45 || $[107] !== t49) {
    t50 = <div>{t43}{t45}{t49}</div>;
    $[105] = t43;
    $[106] = t45;
    $[107] = t49;
    $[108] = t50;
  } else {
    t50 = $[108];
  }
  let t51;
  if ($[109] !== t41 || $[110] !== t50) {
    t51 = <section className="mt-12 grid max-w-7xl items-center gap-12 px-4 lg:mx-auto lg:mt-40 lg:grid-cols-2">{t41}{t50}</section>;
    $[109] = t41;
    $[110] = t50;
    $[111] = t51;
  } else {
    t51 = $[111];
  }
  let t52;
  if ($[112] !== t39 || $[113] !== t51) {
    t52 = <div className="relative z-30 bg-blue-100 pt-14 pb-20 lg:pt-20 lg:pb-40">{t39}{t51}</div>;
    $[112] = t39;
    $[113] = t51;
    $[114] = t52;
  } else {
    t52 = $[114];
  }
  let t53;
  if ($[115] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <SectionDelimiter color="bg-blue-50" background="bg-blue-100" />;
    $[115] = t53;
  } else {
    t53 = $[115];
  }
  let t54;
  if ($[116] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <div className="py-10 lg:py-20"><FaqIntro link={LINKS().faq} /></div>;
    $[116] = t54;
  } else {
    t54 = $[116];
  }
  let t55;
  if ($[117] !== isCsLanguage || $[118] !== t) {
    t55 = <div className="relative z-10">{t54}<MoreInfo buttons={isCsLanguage ? [{
        analyticsParam: "mobileAppMoreInfoDeferredPayment",
        icon: <IconDeferredPayment className="fillButtonIcon" />,
        testId: "mobileAppMoreInfoDeferredPayment",
        title: t("core:products.deferredPayment"),
        to: LINKS().deferredPayment
      }, {
        analyticsParam: "mobileAppMoreInfoRevolvingApplication",
        icon: <IconMaxi className="fillButtonIcon" />,
        testId: "mobileAppMoreInfoRevolvingApplication",
        title: t("core:products.revolving"),
        to: LINKS().revolving
      }, {
        analyticsParam: "mobileAppMoreInfoPayInThree",
        icon: <IconThird className="fillButtonIcon" />,
        testId: "mobileAppMoreInfoPayInThree",
        title: t("core:products.payInThree"),
        to: LINKS().payInThree
      }, {
        analyticsParam: "mobileAppMoreInfoCard",
        icon: <IconCard className="fillButtonIcon" />,
        testId: "mobileAppMoreInfoCard",
        title: t("core:products.card"),
        to: LINKS().card
      }] : [{
        analyticsParam: "mobileAppMoreInfoPayInThree",
        icon: <IconThird className="fillButtonIcon" />,
        testId: "mobileAppMoreInfoPayInThree",
        title: t("core:products.payInThree"),
        to: LINKS().payInThree
      }, {
        analyticsParam: "mobileAppMoreInfoWhereToShop",
        icon: <IconShoppingCart className="fillButtonIcon" />,
        testId: "mobileAppMoreInfoWhereToShop",
        title: t("core:products.whereToShop"),
        to: LINKS().whereToShop
      }, {
        analyticsParam: "mobileAppMobileApp",
        icon: <IconMobile className="fillButtonIcon" />,
        testId: "mobileAppMoreInfoMobileApp",
        title: t("core:products.mobileApp"),
        to: LINKS().mobileApp
      }, {
        analyticsParam: "mobileAppMoreInfoAboutCompany",
        icon: <IconUser className="fillButtonIcon" />,
        testId: "mobileAppMoreInfoAboutCompany",
        title: t("core:link.aboutUs"),
        to: LINKS().aboutCompany
      }]} /></div>;
    $[117] = isCsLanguage;
    $[118] = t;
    $[119] = t55;
  } else {
    t55 = $[119];
  }
  let t56;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = <div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" />;
    $[120] = t56;
  } else {
    t56 = $[120];
  }
  let t57;
  if ($[121] !== t55) {
    t57 = <div className="relative overflow-hidden bg-blue-50">{t55}{t56}</div>;
    $[121] = t55;
    $[122] = t57;
  } else {
    t57 = $[122];
  }
  let t58;
  if ($[123] !== menuPromoBanners || $[124] !== t16 || $[125] !== t3 || $[126] !== t52 || $[127] !== t57) {
    t58 = <Layout menuPromoBanners={menuPromoBanners}>{t3}{t4}{t16}{t17}{t52}{t53}{t57}</Layout>;
    $[123] = menuPromoBanners;
    $[124] = t16;
    $[125] = t3;
    $[126] = t52;
    $[127] = t57;
    $[128] = t58;
  } else {
    t58 = $[128];
  }
  return t58;
}
export const getStaticProps = (async ctx => {
  return {
    props: {
      pageBanner: await getPageBannerBySlug('mobile-app'),
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      ...(await getIntlProps(ctx, ['mobileApp']))
    }
  };
}) satisfies GetStaticProps<Props>;